package com.nonopn.skydome.common.app.pages.estimates

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Switch
import androidx.compose.material3.SwitchDefaults
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import com.nonopn.skydome.common.app.api.impl.ADMIN_PAYMENT_DEMO
import com.nonopn.skydome.common.app.api.impl.ADMIN_PAYMENT_EXTERNAL
import com.nonopn.skydome.common.app.api.impl.ADMIN_PAYMENT_STRIPE
import com.nonopn.skydome.common.app.api.impl.ThunderDetectionEstimate
import com.nonopn.skydome.common.app.api.impl.canToggleActive
import com.nonopn.skydome.common.app.pages.probes.isProbeDeleted
import com.nonopn.skywatcher.common.app.res.Res
import com.nonopn.skywatcher.common.app.res.estimate_created_on
import com.nonopn.skywatcher.common.app.res.estimate_enabled_off
import com.nonopn.skywatcher.common.app.res.estimate_enabled_on
import com.nonopn.skywatcher.common.app.res.estimate_issue_external_subscription
import com.nonopn.skywatcher.common.app.res.estimate_issue_probe_app
import com.nonopn.skywatcher.common.app.res.estimate_issue_probe_demo
import com.nonopn.skywatcher.common.app.res.estimate_issue_probe_end
import com.nonopn.skywatcher.common.app.res.estimate_issue_probe_expired
import com.nonopn.skywatcher.common.app.res.estimate_issue_probe_owner
import com.nonopn.skywatcher.common.app.res.estimate_issue_subscription_quantity
import com.nonopn.skywatcher.common.app.res.estimate_issue_unpaid_active_probes
import com.nonopn.skywatcher.common.app.res.estimate_payment_demo
import com.nonopn.skywatcher.common.app.res.estimate_payment_external
import com.nonopn.skywatcher.common.app.res.estimate_payment_stripe
import com.nonopn.skywatcher.common.app.res.estimate_probe_default_name
import com.nonopn.skywatcher.common.app.res.estimate_probe_inactive
import com.nonopn.skywatcher.common.app.res.estimate_probes_count_label
import com.nonopn.skywatcher.common.app.res.estimate_ref
import com.nonopn.skywatcher.common.app.res.estimate_status_active
import com.nonopn.skywatcher.common.app.res.estimate_status_demo
import com.nonopn.skywatcher.common.app.res.estimate_status_demo_expired
import com.nonopn.skywatcher.common.app.res.estimate_status_empty
import com.nonopn.skywatcher.common.app.res.estimate_status_ended
import com.nonopn.skywatcher.common.app.res.estimate_status_expired
import com.nonopn.skywatcher.common.app.res.estimate_status_external_active
import com.nonopn.skywatcher.common.app.res.estimate_status_external_disabled
import com.nonopn.skywatcher.common.app.res.estimate_status_external_expired
import com.nonopn.skywatcher.common.app.res.estimate_status_external_partial
import com.nonopn.skywatcher.common.app.res.estimate_status_payment_issue
import com.nonopn.skywatcher.common.app.res.estimate_status_pending_payment
import com.nonopn.skywatcher.common.app.res.estimate_toggle_readonly_demo
import com.nonopn.skywatcher.common.app.res.estimate_toggle_readonly_stripe
import com.nonopn.skywatcher.common.app.res.estimate_valid_until
import com.nonopn.skywatcher.design.local.LocalFontSizes
import com.nonopn.skywatcher.design.widgets.DefaultCard
import com.nonopn.skywatcher.design.widgets.defaultCardBackground
import eu.codlab.compose.widgets.TextNormal
import korlibs.time.DateTime
import kotlin.math.round
import org.jetbrains.compose.resources.StringResource
import org.jetbrains.compose.resources.stringResource
import kotlin.uuid.ExperimentalUuidApi

private val cornerSide = 4.dp

private object Colors {
    val statusActiveBackground = Color(0xFFE8F5E9)
    val statusActiveText = Color(0xFF1B5E20)
    val statusPendingBackground = Color(0xFFFFF8E1)
    val statusPendingText = Color(0xFF7A5000)
    val statusExpiredBackground = Color(0xFFF5F5F5)
    val statusExpiredText = Color(0xFF424242)
    val statusExternalActiveBackground = Color(0xFFE3F2FD)
    val statusExternalActiveText = Color(0xFF0D47A1)
    val statusExternalDisabledBackground = Color(0xFFFFE9D6)
    val statusExternalDisabledText = Color(0xFF8A3B00)
    val statusEmptyBackground = Color(0xFFECEFF1)
    val statusEmptyText = Color(0xFF37474F)
    val statusDemoBackground = Color(0xFFEDE7F6)
    val statusDemoText = Color(0xFF4527A0)
    val statusPartialBackground = Color(0xFFE0F2F1)
    val statusPartialText = Color(0xFF004D40)
    val statusIssueBackground = Color(0xFFFFEBEE)
    val statusIssueText = Color(0xFFB71C1C)
    val issueBackground = Color(0xFFFFF3CD)
    val issueText = Color(0xFF5C3B00)
    val metaText = Color(0xFF5B6678)
    val probeCardBackground = Color(0xFFF2F5FA)
    // Text on the (always light) probe cards and the toggle colors: readable in light and dark themes
    val probeCardText = Color(0xFF1A2433)
    val probeCardSubText = Color(0xFF4A5568)
    val switchOn = Color(0xFF2E7D32)
    val switchOffTrack = Color(0xFF8A94A6)
}

// Format unix seconds to DD/MM/YYYY
private fun Long.toShortDate(): String {
    val dt = DateTime(this * 1000.0)
    val day = dt.dayOfMonth.toString().padStart(2, '0')
    val month = dt.month.index1.toString().padStart(2, '0')
    return "$day/$month/${dt.year}"
}

// Coordinates rounded to 4 decimals (about 10 m)
private fun Double.rounded() = round(this * 10000.0) / 10000.0

private class BadgeStyle(val background: Color, val text: Color)

private fun EstimateStatus.badgeStyle(): BadgeStyle = when (this) {
    EstimateStatus.ACTIVE -> BadgeStyle(Colors.statusActiveBackground, Colors.statusActiveText)
    EstimateStatus.PENDING_PAYMENT -> BadgeStyle(Colors.statusPendingBackground, Colors.statusPendingText)
    EstimateStatus.EXPIRED, EstimateStatus.DEMO_EXPIRED, EstimateStatus.EXTERNAL_EXPIRED, EstimateStatus.ENDED ->
        BadgeStyle(Colors.statusExpiredBackground, Colors.statusExpiredText)
    EstimateStatus.EXTERNAL_ACTIVE ->
        BadgeStyle(Colors.statusExternalActiveBackground, Colors.statusExternalActiveText)
    EstimateStatus.EXTERNAL_DISABLED ->
        BadgeStyle(Colors.statusExternalDisabledBackground, Colors.statusExternalDisabledText)
    EstimateStatus.EXTERNAL_PARTIAL -> BadgeStyle(Colors.statusPartialBackground, Colors.statusPartialText)
    EstimateStatus.EMPTY -> BadgeStyle(Colors.statusEmptyBackground, Colors.statusEmptyText)
    EstimateStatus.DEMO -> BadgeStyle(Colors.statusDemoBackground, Colors.statusDemoText)
    EstimateStatus.PAYMENT_ISSUE -> BadgeStyle(Colors.statusIssueBackground, Colors.statusIssueText)
}

private fun EstimateStatus.labelRes(): StringResource = when (this) {
    EstimateStatus.ACTIVE -> Res.string.estimate_status_active
    EstimateStatus.PENDING_PAYMENT -> Res.string.estimate_status_pending_payment
    EstimateStatus.EXPIRED -> Res.string.estimate_status_expired
    EstimateStatus.EXTERNAL_ACTIVE -> Res.string.estimate_status_external_active
    EstimateStatus.EXTERNAL_DISABLED -> Res.string.estimate_status_external_disabled
    EstimateStatus.EXTERNAL_PARTIAL -> Res.string.estimate_status_external_partial
    EstimateStatus.EXTERNAL_EXPIRED -> Res.string.estimate_status_external_expired
    EstimateStatus.EMPTY -> Res.string.estimate_status_empty
    EstimateStatus.DEMO -> Res.string.estimate_status_demo
    EstimateStatus.DEMO_EXPIRED -> Res.string.estimate_status_demo_expired
    EstimateStatus.PAYMENT_ISSUE -> Res.string.estimate_status_payment_issue
    EstimateStatus.ENDED -> Res.string.estimate_status_ended
}

private fun EstimateIssueKind.labelRes(): StringResource = when (this) {
    EstimateIssueKind.PROBE_OWNER -> Res.string.estimate_issue_probe_owner
    EstimateIssueKind.PROBE_APP -> Res.string.estimate_issue_probe_app
    EstimateIssueKind.PROBE_DEMO -> Res.string.estimate_issue_probe_demo
    EstimateIssueKind.PROBE_END -> Res.string.estimate_issue_probe_end
    EstimateIssueKind.PROBE_EXPIRED -> Res.string.estimate_issue_probe_expired
    EstimateIssueKind.UNPAID_ACTIVE_PROBES -> Res.string.estimate_issue_unpaid_active_probes
    EstimateIssueKind.SUBSCRIPTION_QUANTITY -> Res.string.estimate_issue_subscription_quantity
    EstimateIssueKind.EXTERNAL_SUBSCRIPTION -> Res.string.estimate_issue_external_subscription
}

@OptIn(ExperimentalUuidApi::class)
@Composable
fun RenderEstimate(
    modifier: Modifier = Modifier,
    estimate: ThunderDetectionEstimate,
    // Admin mode: show the probe cards of the estimate and the activate / deactivate switch
    showProbes: Boolean = false,
    onToggleEnabled: ((ThunderDetectionEstimate, Boolean) -> Unit)? = null,
    onClick: (ThunderDetectionEstimate) -> Unit,
) {
    val status = estimate.resolveStatus(DateTime.now().unixMillisLong / 1000L)

    val style = status.badgeStyle()
    val bgColor = style.background
    val textColor = style.text
    val statusLabel = stringResource(status.labelRes())

    Column(
        modifier.padding(cornerSide)
            .clip(RoundedCornerShape(cornerSide))
    ) {
        DefaultCard(
            modifier = Modifier.fillMaxWidth(),
            columnModifier = Modifier.padding(16.dp),
            elevation = 10.dp,
            backgroundColor = defaultCardBackground(),
            onClick = { onClick(estimate) },
        ) {
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically,
            ) {
                TextNormal(
                    modifier = Modifier.weight(1f),
                    text = estimate.email,
                    fontWeight = FontWeight.Bold,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis,
                    fontSize = LocalFontSizes.current.administrators.cardUserEmail,
                )

                Spacer(Modifier.width(8.dp))

                // Status badge
                Box(
                    modifier = Modifier
                        .clip(RoundedCornerShape(12.dp))
                        .background(bgColor)
                        .padding(horizontal = 10.dp, vertical = 4.dp),
                ) {
                    TextNormal(
                        text = statusLabel,
                        color = textColor,
                        fontWeight = FontWeight.Bold,
                        fontSize = LocalFontSizes.current.map.callOutNormal,
                    )
                }
            }

            Spacer(Modifier.height(8.dp))

            // Probe count
            TextNormal(
                text = "${estimate.visibleProbeCount()} ${stringResource(Res.string.estimate_probes_count_label)}",
                fontSize = LocalFontSizes.current.map.callOutNormal,
                fontWeight = FontWeight.Bold,
            )

            // Creation date (oldest probe, when known) and short reference of the estimate
            Spacer(Modifier.height(4.dp))
            TextNormal(
                text = listOfNotNull(
                    estimate.createdAtSecondsUtc()?.let {
                        stringResource(Res.string.estimate_created_on, it.toShortDate())
                    },
                    stringResource(Res.string.estimate_ref, shortId(estimate.uuid)),
                ).joinToString(" · "),
                fontSize = LocalFontSizes.current.map.callOutNormal,
                fontWeight = FontWeight.Normal,
            )

            // Payment type of the estimate (admin route only)
            estimate.admin?.let { info ->
                val paymentLabel = when (info.payment) {
                    ADMIN_PAYMENT_DEMO -> Res.string.estimate_payment_demo
                    ADMIN_PAYMENT_EXTERNAL -> Res.string.estimate_payment_external
                    ADMIN_PAYMENT_STRIPE -> Res.string.estimate_payment_stripe
                    else -> null
                }
                if (paymentLabel != null) {
                    Spacer(Modifier.height(4.dp))
                    TextNormal(
                        text = stringResource(paymentLabel),
                        fontSize = LocalFontSizes.current.map.callOutNormal,
                        fontWeight = FontWeight.Normal,
                    )
                }
            }

            // Expiry date if set
            if (estimate.validUntilSecondsUtc != null) {
                Spacer(Modifier.height(4.dp))
                TextNormal(
                    text = "${stringResource(Res.string.estimate_valid_until)} " +
                        estimate.validUntilSecondsUtc.toShortDate(),
                    fontSize = LocalFontSizes.current.map.callOutNormal,
                    fontWeight = FontWeight.Normal,
                )
            }

            if (onToggleEnabled != null && (estimate.admin == null || estimate.canToggleActive())) {
                Spacer(Modifier.height(4.dp))
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.SpaceBetween,
                    verticalAlignment = Alignment.CenterVertically,
                ) {
                    TextNormal(
                        text = stringResource(
                            if (estimate.areEnabled) Res.string.estimate_enabled_on else Res.string.estimate_enabled_off
                        ),
                        fontSize = LocalFontSizes.current.map.callOutNormal,
                        fontWeight = FontWeight.Bold,
                    )
                    Switch(
                        checked = estimate.areEnabled,
                        onCheckedChange = { onToggleEnabled(estimate, it) },
                        colors = SwitchDefaults.colors(
                            checkedThumbColor = Color.White,
                            checkedTrackColor = Colors.switchOn,
                            checkedBorderColor = Colors.switchOn,
                            uncheckedThumbColor = Color.White,
                            uncheckedTrackColor = Colors.switchOffTrack,
                            uncheckedBorderColor = Colors.switchOffTrack,
                        ),
                    )
                }
            }

            val adminInfo = estimate.admin
            if (onToggleEnabled != null && adminInfo != null && !estimate.canToggleActive()) {
                // Stripe: the subscription of the customer decides. Demo: active until its end date.
                val readOnlyLabel = when (adminInfo.payment) {
                    ADMIN_PAYMENT_STRIPE -> Res.string.estimate_toggle_readonly_stripe
                    ADMIN_PAYMENT_DEMO -> Res.string.estimate_toggle_readonly_demo
                    else -> null
                }
                if (readOnlyLabel != null) {
                    Spacer(Modifier.height(4.dp))
                    TextNormal(
                        text = stringResource(readOnlyLabel),
                        fontSize = LocalFontSizes.current.map.callOutNormal,
                        fontWeight = FontWeight.Bold,
                    )
                }
            }

            // Anomalies detected by the server (readable on any theme: own background)
            if (showProbes) {
                estimate.admin?.issues?.forEach { issue ->
                    Spacer(Modifier.height(6.dp))
                    val kind = estimateIssueKind(issue.code)
                    val text = (kind?.let { stringResource(it.labelRes()) } ?: issue.message.ifBlank { issue.code }) +
                        (issue.probe?.let { " · ${shortId(it)}" } ?: "")
                    Box(
                        modifier = Modifier
                            .fillMaxWidth()
                            .clip(RoundedCornerShape(8.dp))
                            .background(Colors.issueBackground)
                            .padding(horizontal = 10.dp, vertical = 6.dp),
                    ) {
                        TextNormal(
                            text = "\u26A0 $text",
                            color = Colors.issueText,
                            fontSize = LocalFontSizes.current.map.callOutNormal,
                        )
                    }
                }
            }

            if (showProbes) {
                // Read-only cards: activation is only done per estimate (areEnabled), never per probe
                estimate.probes.filterNot { isProbeDeleted(it.status) }.forEachIndexed { index, probe ->
                    Spacer(Modifier.height(6.dp))
                    Row(
                        modifier = Modifier
                            .fillMaxWidth()
                            .clip(RoundedCornerShape(8.dp))
                            .background(Colors.probeCardBackground)
                            .padding(horizontal = 10.dp, vertical = 6.dp),
                        horizontalArrangement = Arrangement.SpaceBetween,
                        verticalAlignment = Alignment.CenterVertically,
                    ) {
                        Column(modifier = Modifier.weight(1f)) {
                            TextNormal(
                                text = readableProbeName(probe.name)
                                    ?: stringResource(Res.string.estimate_probe_default_name, index + 1),
                                color = Colors.probeCardText,
                                fontWeight = FontWeight.Bold,
                                maxLines = 1,
                                overflow = TextOverflow.Ellipsis,
                                fontSize = LocalFontSizes.current.map.callOutNormal,
                            )
                            TextNormal(
                                text = "${probe.latitude.rounded()}, ${probe.longitude.rounded()}" +
                                    if (readableProbeName(probe.name) == null) " · ${shortId(probe.uuid)}" else "",
                                color = Colors.probeCardSubText,
                                fontSize = LocalFontSizes.current.map.callOutNormal,
                            )
                            probe.validUntilSecondsUtc?.let { end ->
                                TextNormal(
                                    text = "${stringResource(Res.string.estimate_valid_until)} ${end.toShortDate()}",
                                    color = Colors.probeCardSubText,
                                    fontSize = LocalFontSizes.current.map.callOutNormal,
                                )
                            }
                        }

                        Spacer(Modifier.width(8.dp))

                        // Real status of the probe (Active / Inactive); falls back to the estimate status
                        val probeActive = when (probe.status?.trim()?.lowercase()) {
                            "active" -> true
                            "inactive" -> false
                            else -> status == EstimateStatus.ACTIVE
                        }
                        val probeBg = if (probeActive) Colors.statusActiveBackground else Colors.statusExpiredBackground
                        val probeText = if (probeActive) Colors.statusActiveText else Colors.statusExpiredText

                        Box(
                            modifier = Modifier
                                .clip(RoundedCornerShape(12.dp))
                                .background(probeBg)
                                .padding(horizontal = 8.dp, vertical = 2.dp),
                        ) {
                            TextNormal(
                                text = stringResource(
                                    if (probeActive) {
                                        Res.string.estimate_status_active
                                    } else {
                                        Res.string.estimate_probe_inactive
                                    }
                                ),
                                color = probeText,
                                fontWeight = FontWeight.Bold,
                                fontSize = LocalFontSizes.current.map.callOutNormal,
                            )
                        }
                    }
                }
            }
        }
    }
}
